import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Settings2, Plus, Save, X, Variable } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DocumentPremissas = ({ documentId, canEdit }) => {
  const [premissas, setPremissas] = useState({});
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [newVarName, setNewVarName] = useState('');
  const [saving, setSaving] = useState(false);
  const [catalogVariables, setCatalogVariables] = useState([]);

  const loadPremissas = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/documents/${documentId}/premissas`, { withCredentials: true });
      setPremissas(res.data || {});
    } catch {
      setPremissas({});
    }
  }, [documentId]);

  const loadVariablesCatalog = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/variables`, { withCredentials: true });
      setCatalogVariables(res.data || []);
    } catch {
      setCatalogVariables([]);
    }
  }, []);

  useEffect(() => { loadPremissas(); loadVariablesCatalog(); }, [loadPremissas, loadVariablesCatalog]);

  const startEdit = () => {
    setEditData({ ...premissas });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/documents/${documentId}/premissas`, { premissas: editData }, { withCredentials: true });
      toast.success('Premissas salvas');
      setPremissas(editData);
      setEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar premissas');
    } finally {
      setSaving(false);
    }
  };

  const addVariable = (varName) => {
    const name = varName.trim().toUpperCase().replace(/[^A-Z0-9_]/g, '_');
    if (!name || editData[name] !== undefined) return;
    setEditData({ ...editData, [name]: '' });
    setNewVarName('');
  };

  const removeVariable = (key) => {
    const updated = { ...editData };
    delete updated[key];
    setEditData(updated);
  };

  const getVarLabel = (key) => {
    const found = catalogVariables.find(v => v.name === key);
    return found?.label || key;
  };

  const hasData = Object.keys(premissas).length > 0;

  // Group catalog variables by category for quick-add
  const groupedCatalog = {};
  catalogVariables.filter(v => editData[v.name] === undefined).forEach(v => {
    const cat = v.category || 'geral';
    if (!groupedCatalog[cat]) groupedCatalog[cat] = [];
    groupedCatalog[cat].push(v);
  });

  return (
    <div className="bg-white border border-[#E5E5EA] rounded-xl p-5" data-testid="document-premissas">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-[#111111] flex items-center gap-2">
          <Variable className="w-4 h-4 text-[#AF2628]" />
          Premissas do Documento
        </h3>
        {canEdit && !editing && (
          <Button variant="ghost" size="sm" className="h-7 text-xs text-[#AF2628]" onClick={startEdit} data-testid="edit-premissas-btn">
            <Settings2 className="w-3.5 h-3.5 mr-1" />
            {hasData ? 'Editar' : 'Configurar'}
          </Button>
        )}
      </div>

      {!editing && !hasData && (
        <p className="text-sm text-[#AEAEB2]">Nenhuma premissa configurada. Configure para usar formulas de calculo.</p>
      )}

      {!editing && hasData && (
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(premissas).map(([key, value]) => (
            <div key={key} className="bg-[#F7F7F8] rounded-lg px-3 py-2">
              <span className="text-[10px] text-[#6E6E73] block">{getVarLabel(key)}</span>
              <span className="text-sm font-semibold text-[#111111]">{value || '-'}</span>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="space-y-3">
          {/* Quick add from catalog */}
          {Object.keys(groupedCatalog).length > 0 && (
            <div>
              <p className="text-xs text-[#6E6E73] mb-2">Variaveis do catalogo (clique para adicionar):</p>
              <div className="max-h-28 overflow-y-auto space-y-1.5">
                {Object.entries(groupedCatalog).map(([cat, vars]) => (
                  <div key={cat} className="flex flex-wrap gap-1">
                    {vars.map(v => (
                      <button
                        key={v.var_id}
                        onClick={() => setEditData({ ...editData, [v.name]: v.default_value || '' })}
                        className="text-[10px] px-2 py-1 rounded bg-[#F7F7F8] text-[#6E6E73] hover:bg-[#AF2628] hover:text-white transition-colors"
                        title={v.label}
                      >
                        + {v.name}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Edit existing variables */}
          <div className="space-y-2">
            {Object.entries(editData).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2">
                <div className="w-44 flex-shrink-0">
                  <Label className="text-[10px] text-[#6E6E73]">{getVarLabel(key)}</Label>
                  <span className="text-[9px] font-mono text-[#AEAEB2] block">{key}</span>
                </div>
                <Input
                  value={value}
                  onChange={(e) => setEditData({ ...editData, [key]: e.target.value })}
                  className="flex-1 text-sm"
                  placeholder="Valor"
                  data-testid={`premissa-${key}`}
                />
                <button onClick={() => removeVariable(key)} className="text-red-400 hover:text-red-600 p-1">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>

          {/* Add custom variable */}
          <div className="flex items-center gap-2">
            <Input
              value={newVarName}
              onChange={(e) => setNewVarName(e.target.value)}
              placeholder="Nome da variavel personalizada"
              className="flex-1 text-xs"
              onKeyDown={(e) => e.key === 'Enter' && addVariable(newVarName)}
              data-testid="new-premissa-name"
            />
            <Button size="sm" variant="outline" onClick={() => addVariable(newVarName)} data-testid="add-premissa-btn">
              <Plus className="w-3 h-3 mr-1" /> Adicionar
            </Button>
          </div>

          {/* Actions */}
          <div className="flex gap-2 justify-end pt-2 border-t border-[#E5E5EA]">
            <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancelar</Button>
            <Button size="sm" className="bg-[#AF2628] hover:bg-[#961B22]" onClick={handleSave} disabled={saving} data-testid="save-premissas-btn">
              <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentPremissas;
